import React, { useRef } from "react";
import { Box, GhostIconButton, Loader, Text } from "components";
import Drawer from "components/smaltOverwritten/Drawer";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import useWindowDimensions from "hooks/windowDimensions";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getTranslationsStyle } from "utils/arabisation.util";

import EligibleContent from "./EligibleContent";
import NotEligibleContent from "./NotEligibleContent";

export const CircleButtonStyled = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1200px;
  height: 700px;
  border-radius: 50%;
  background-color: #686ea0;
  border: none;
  position: absolute;
  left: ${({ isArabic }) => (!isArabic ? "-540px" : "unset")};
  right: ${({ isArabic }) => (isArabic ? "-540px" : "unset")};
  top: -25px;
  z-index: -1;
`;

const ValidationDrawer = ({
  isOpen,
  onClose,
  validationResult,
  isValidating,
  onContinue,
  scanAnalytics,
  nationality,
  gender,
  dateOfBirth,
}) => {
  const { sendAnalytics } = useTrackPageAnalytics();
  const { t, i18n } = useTranslation();
  const { height } = useWindowDimensions();
  const resizeHandleRef = useRef(null);

  const handleScanAnother = () => {
    // The current session was already submitted on the validation response.
    // Start a fresh session linked to it.
    scanAnalytics?.chainNext?.();
    onClose();
  };

  const renderContent = () => {
    if (isValidating) {
      return (
        <Box
          d="flex"
          direction="column"
          justifyContent="center"
          alignItems="center"
          flex="1"
          p="l"
          gap="m"
        >
          <Loader />
          <Text
            fontSize="m"
            color="primary"
            style={getTranslationsStyle(i18n, { textAlign: "center" })}
          >
            {t("smart_gate.validating_passport")}
          </Text>
        </Box>
      );
    }

    if (!validationResult) {
      return null;
    }

    const isEligible =
      validationResult.status === "success" && validationResult.data.result;

    if (isEligible) {
      return (
        <EligibleContent
          onContinue={onContinue}
          onScanAnother={handleScanAnother}
        />
      );
    }

    return (
      <NotEligibleContent
        onContinue={onContinue}
        onScanAnother={handleScanAnother}
      />
    );
  };

  return (
    <Drawer isOpen={isOpen} translationAmount={0} minimumContentHeight={height}>
      <Box d="flex" direction="column" alignItems="center" pH="m">
        <Box
          ref={resizeHandleRef}
          h="24px"
          d="flex"
          justifyContent="center"
          alignItems="center"
          style={{ cursor: "pointer" }}
        >
          <Box
            w="40px"
            h="4px"
            backgroundColor="resizeHandle"
            borderRadius="2px"
            style={{ opacity: 0.8 }}
          />
        </Box>
        <Box
          h="40px"
          d="flex"
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          borderColor="lightNeutrals"
          mB="s"
        >
          <GhostIconButton
            name="clearclose"
            color="inherit"
            iconSize="l"
            onClick={() => {
              sendAnalytics({
                key: "smart-gate-validation-click-close",
                type: "event",
                name: "User clicked on 'X' button in Validation drawer and the scanning process is started again",
              });
              handleScanAnother();
            }}
          />
        </Box>
      </Box>
      <Box
        d="flex"
        direction="column"
        style={{ height: "calc(100dvh - 76px)" }}
      >
        {renderContent()}
      </Box>
    </Drawer>
  );
};

export default ValidationDrawer;
